import { Injectable, inject } from '@angular/core';
import BaseHttpService from '@hp-core/services/base-http.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  map,
  of,
  catchError,
  throwError,
  ReplaySubject,
  finalize,
} from 'rxjs';
import {
  ICheckoutCounterItem,
  ICheckoutItem,
  ICheckoutCounterItemV2,
  ICheckoutStatus,
  IPaymentRequestMinimal,
  IPaymentRequestResponse,
  IReviewCheckoutCounter,
  ITransactionV2,
  TModifiedCheckoutCounterQueries,
  ICheckoutCounterQueries,
} from '../_types/types';
import CheckoutStatusAdapter from '../_adapters/get-checkout-status.adapter';
import { PostRequestPaymentAdapter } from '../_adapters/post-request-payment.adapter';
import { UserStoreService } from '@hp-core/services/user-store.service';
import { CheckoutStoreService } from './checkout-store.service';
import { ICriteriaObject, IPaginationQuery } from '@hp-core/types/types';
import PaginationQueryAdapter from '@hp-core/adapters/pagination-query.adapter';
import { switchMap } from 'rxjs/operators';
import { IMovementResponse, PosItem } from '@hp-movements/_types/types';

@Injectable({
  providedIn: 'root',
})
export class CheckoutApiService extends BaseHttpService {
  private _chachedQrData: string | null = null;
  private _cachedUser: string | null = null;
  private readonly _userStore = inject(UserStoreService);
  private readonly cache$: ReplaySubject<CheckoutStatusAdapter> = new ReplaySubject(1);
  private requestInProgress: boolean = false;

  constructor(
    private readonly _http: HttpClient,
    private readonly _checkoutStore: CheckoutStoreService
  ) {
    super();
  }

  public initQrPayment() {
    return this.httpPostPaymentRequest(1000, 'GENERACIÓN QR FIJO', 0, 'QR');
  }

  /**
   * Get QR identificator
   * @description Returns qr data of api and store data in cache.
   * Second call to method will return store cached data
   */
  httpGetQrData(): Observable<string> {
    return this._userStore.getUsername().pipe(
      switchMap((user) => {
        if (this._chachedQrData && this._cachedUser === user) {
          return of(this._chachedQrData);
        }

        this._cachedUser = user;

        return this._http
          .get<{ qr_data: string }>(
            this.getUri({}, 'cobro_presencial', 'checkout', 'get-qr-cliente')
          )
          .pipe(
            switchMap((response) => {
              if (!response.qr_data) {
                // Si qr_data está vacío, nulo o indefinido, llama a initQrPayment
                return this.initQrPayment().pipe(
                  map((paymentResponse) => {
                    const generatedQrData = paymentResponse.qr_data;
                    this._chachedQrData = generatedQrData; // Almacena en caché
                    return generatedQrData;
                  })
                );
              } else {
                this._chachedQrData = response.qr_data;
                return of(response.qr_data);
              }
            }),
            catchError((error: HttpErrorResponse) => {
              return throwError(() => ({
                message: 'Ocurrió un error: ' + error.message,
              }));
            })
          );
      })
    );
  }

  /**
   * httpGet Checkout status (v1)
   */
  httpGetCheckoutStatus(): Observable<CheckoutStatusAdapter> {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this._http
        .get<ICheckoutStatus>(
          this.getUri({}, 'cobro_presencial', 'checkout', 'get-estado-checkout')
        )
        .pipe(
          map((value) => {
            this._checkoutStore.ccId = value.cajaResponse?.id ?? 0;
            return new CheckoutStatusAdapter(
              value.cajaResponse?.id,
              value.cajaResponse?.closed,
              value.pos,
              value.pvResponse?.descripcion,
              value.pvResponse?.terminalUserId ?? 0,
              value.servicioCobranza,
              value.funcionalidadCaja
            );
          }),
          catchError((error: HttpErrorResponse) => {
            return throwError(() => ({
              message: 'Ocurrió un error: ' + error.message,
            }));
          }),
          finalize(() => {
            this.requestInProgress = false;
          })
        )
        .subscribe({
          next: (data) => this.cache$.next(data),
          error: (err) => this.cache$.error(err),
        });
    }
    return this.cache$.asObservable();
  }

  httpPutCloseCounter(caja_id: number) {
    const data = new FormData();
    data.append('caja_id', caja_id.toString());
    return this._http.put<IReviewCheckoutCounter>(
      this.getUri({}, 'cobro_presencial', 'checkout', 'close-caja'),
      data
    );
  }

  /**
   * TODO: When method http is ready make service method
   */
  httpGetCheckoutCounterReview(
    paymentBoxId: number
  ): Observable<IReviewCheckoutCounter> {
    return this._http.get<IReviewCheckoutCounter>(
      this.getUri(
        {},
        'cobro_presencial',
        'checkout',
        'get-cierre-caja-total-by-caja',
        paymentBoxId.toString()
      )
    );
  }

  httpGetAttachedTransactions(
    checkoutCounterId: number,
    pagination: Partial<IPaginationQuery>
  ): Observable<ICriteriaObject<ICheckoutCounterItem>> {
    const paginationModel = new PaginationQueryAdapter(
      pagination.pageNumber,
      pagination.pageSize,
      pagination.sortDirection
    );
    // "/api/cobro_presencial/checkout/get-movimientos?caja_id=11&solicitud_pago_estado_id=&cliente_id=&user_role=&from=&to="
    return this._http.get<ICriteriaObject<ICheckoutCounterItem>>(
      this.getUri(
        {
          ...paginationModel,
          caja_id: checkoutCounterId,
          solicitud_pago_estado_id: 2,
        },
        'cobro_presencial',
        'checkout',
        'get-movimientos'
      )
    );
  }

  /**
   * Get an sp by their id
   * @param id
   */
  httpGetSpById(id: number): Observable<ITransactionV2> {
    return this._http.get<ITransactionV2>(
      this.getUri(
        {},
        'dashboard_cli',
        'v2',
        'movimientos',
        'solicitud_pago',
        id.toString()
      )
    );
  }

  /**
   * Get an sp by their id
   * @param id
   */
  httpPostSpByExtReference(
    externalReference: string
  ): Observable<ICriteriaObject<IMovementResponse>> {
    return this._http.post<ICriteriaObject<IMovementResponse>>(
      this.getUri(
        {
          pageNumber: 0,
          pageSize: 10,
          sortDirection: 'ASC',
        },
        'dashboard_cli',
        'v2',
        'movimientos',
        'solicitud_pago'
      ),
      { referencia_externa: externalReference }
    );
  }

  /**
   * Get an sp by their id
   * @param id
   */
  httpPostSpByExtReference2(
    externalReference: string
  ): Observable<ICriteriaObject<IMovementResponse>> {
    return this._http.post<ICriteriaObject<IMovementResponse>>(
      this.getUri(
        {
          pageNumber: 0,
          pageSize: 10,
          sortDirection: 'ASC',
        },
        'dashboard_cli',
        'v2',
        'movimientos',
        'solicitud_pago'
      ),
      { referencia_externa_2: externalReference }
    );
  }

  /**
   * Paginated (v1) /checkout/list
   * @param checkoutQueries
   */
  httpGetCheckoutCounterList(
    checkoutQueries: Partial<ICheckoutCounterQueries>
  ): Observable<ICriteriaObject<ICheckoutItem>> {
    const { sortDirection, pageSize, pageNumber, from, to } = checkoutQueries;

    const pagination = new PaginationQueryAdapter(
      pageNumber,
      pageSize,
      sortDirection
    );
    const query: TModifiedCheckoutCounterQueries = { ...pagination };

    if (checkoutQueries.from) {
      query.desde = checkoutQueries.from;
    }
    if (checkoutQueries.to) {
      query.hasta = checkoutQueries.to;
    }
    if (checkoutQueries.id) {
      query.id = checkoutQueries.id;
    }
    if (!Number.isNaN(Number(checkoutQueries.pos_id))) {
      query.punto_venta_id = Number(checkoutQueries.pos_id);
    }

    return this._http
      .get<ICriteriaObject<ICheckoutCounterItemV2>>(
        this.getUri(
          { ...query },
          'dashboard_cli',
          'v2',
          'cobro_presencial',
          'cajas'
        )
      )
      .pipe(
        map((data) => {
          return {
            ...data,
            content: data.content.map(
              ({
                id,
                punto_venta_descripcion,
                fecha_cierre,
                fecha_apertura,
              }): ICheckoutItem => ({
                id,
                openDate: fecha_apertura,
                closeDate: fecha_cierre,
                closed: !!fecha_cierre,
                description: punto_venta_descripcion,
              })
            ),
          };
        })
      );
  }

  /**
   * Paginated (v1) /checkout/list/:detail
   *
   * @param checkoutCounterId
   * @param pagination
   */
  httpGetCheckoutTransactionsList(
    checkoutCounterId: number,
    pagination: Partial<IPaginationQuery>
  ): Observable<IPaymentRequestMinimal> {
    const paginationModel = new PaginationQueryAdapter(
      pagination.pageNumber,
      pagination.pageSize,
      pagination.sortDirection
    );
    return this._http.get<IPaymentRequestMinimal>(
      this.getUri(
        {
          ...paginationModel,
          caja_id: checkoutCounterId,
          solicitud_pago_estado_id: 2,
        },
        'cobro_presencial',
        'checkout',
        'get-movimientos'
      )
    );
  }

  /**
   * Get an sp by their id
   * @param id
   */
  httpGetPosList(): Observable<PosItem[]> {
    return this._http.get<PosItem[]>(
      this.getUri(
        {
          pageNumber: 0,
          pageSize: 10,
          sortDirection: 'ASC',
        },
        'dashboard_cli',
        'v2',
        'cobro_presencial',
        'puntos_venta'
      )
    );
  }

  /**
   *
   * genterate qr payment with v1
   */
  httpPostPaymentRequest(
    importe: number,
    descripcion: string,
    cajaId: number,
    type: 'QR' | 'CREDIT' | 'DEBIT' = 'QR',
    spId?: number,
    installmentsQuantity: number = 1
  ) {
    const postPaymentRequest = new PostRequestPaymentAdapter(
      importe,
      window.location.hostname,
      descripcion,
      cajaId,
      spId
    );

    return this._http
      .post<IPaymentRequestResponse>(
        this.getUri(
          {},
          'solicitud_pago',
          'cliente',
          'solicitud_pago_unificado'
        ),
        postPaymentRequest
      )
      .pipe(
        map((resultData) => {
          return resultData;
        })
      );
  }
}
